<template>
  <v-card-text class="pa-6">
    <h3 class="text-h6 mb-4">
      <v-icon left color="error">mdi-account-remove</v-icon>
      การลาออก
    </h3>

    <template v-if="!employee.resignation_date">
      <v-alert type="info" outlined class="mb-4">
        <div class="font-weight-medium">สถานะ: พนักงานคนนี้ยังคงปฏิบัติงานอยู่</div>
        <div class="text-caption">ยังไม่ได้ดำเนินการลาออกหรือเกษียณอายุราชการ</div>
      </v-alert>

      <v-card outlined>
        <v-card-title class="error white--text">
          <v-icon left>mdi-alert</v-icon>
          ฟอร์มการลาออก / เกษียณอายุราชการ
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="resignForm" v-model="formValid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="resignData.date"
                  label="วันที่ลาออก / เกษียณอายุ"
                  type="date"
                  outlined
                  :rules="dateRules"
                  :max="maxDate"
                  hint="เลือกวันที่มีผลการลาออก"
                  persistent-hint
                />
              </v-col>
              
              <v-col cols="12" md="6">
                <v-select
                  v-model="resignData.reason"
                  :items="resignReasons"
                  label="สาเหตุการลาออก"
                  outlined
                  :rules="[v => !!v || 'กรุณาเลือกสาเหตุ']"
                />
              </v-col>
              
              <v-col cols="12">
                <v-textarea
                  v-model="resignData.note"
                  label="หมายเหตุ / รายละเอียดเพิ่มเติม"
                  outlined
                  rows="4"
                  counter="500"
                  :rules="noteRules"
                  hint="ระบุรายละเอียดเพิ่มเติม หรือหมายเหตุประกอบการลาออก"
                  persistent-hint
                />
              </v-col>
            </v-row>

            <!-- แสดงคำเตือนสำคัญ -->
            <v-alert
              type="warning"
              outlined
              dense
              class="my-4"
            >
              <div class="font-weight-medium mb-2">
                <v-icon left small>mdi-alert-circle</v-icon>
                คำเตือนสำคัญ
              </div>
              <ul class="text-caption">
                <li>การดำเนินการลาออกจะมีผลถาวร และไม่สามารถแก้ไขได้ง่าย</li>
                <li>ควรตรวจสอบเอกสารและสิทธิประโยชน์ทั้งหมดก่อนดำเนินการ</li>
                <li>กรุณาแน่ใจว่าข้อมูลวันที่และสาเหตุถูกต้องสมบูรณ์</li>
              </ul>
            </v-alert>

            <div class="d-flex justify-space-between align-center">
              <v-btn
                color="error"
                large
                :loading="resigning"
                :disabled="!formValid"
                @click="handleResign"
              >
                <v-icon left>mdi-account-remove</v-icon>
                ดำเนินการลาออก
              </v-btn>
              
              <v-btn
                text
                color="grey"
                @click="resetForm"
              >
                <v-icon left>mdi-refresh</v-icon>
                ล้างข้อมูล
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </template>

    <template v-else>
      <v-alert type="warning" outlined class="mb-4">
        <div class="d-flex align-center mb-3">
          <v-icon color="warning" class="mr-2">mdi-account-off</v-icon>
          <div class="text-h6">พนักงานคนนี้ได้ลาออกแล้ว</div>
        </div>
        
        <v-divider class="my-3"></v-divider>
        
        <v-row>
          <v-col cols="12" md="4">
            <div class="text-caption grey--text">วันที่ลาออก</div>
            <div class="font-weight-medium">
              <v-icon small class="mr-1">mdi-calendar</v-icon>
              {{ formatDate(employee.resignation_date) }}
            </div>
          </v-col>
          
          <v-col cols="12" md="4">
            <div class="text-caption grey--text">สาเหตุการลาออก</div>
            <div class="font-weight-medium">
              <v-icon small class="mr-1">mdi-information</v-icon>
              {{ employee.resignation_reason }}
            </div>
          </v-col>
          
          <v-col cols="12" md="4">
            <div class="text-caption grey--text">ระยะเวลาการปฏิบัติงาน</div>
            <div class="font-weight-medium">
              <v-icon small class="mr-1">mdi-clock-outline</v-icon>
              {{ calculateWorkDuration() }}
            </div>
          </v-col>
        </v-row>
        
        <v-divider class="my-3"></v-divider>
        
        <div v-if="employee.resignation_note">
          <div class="text-caption grey--text mb-1">หมายเหตุ</div>
          <div class="font-weight-regular">{{ employee.resignation_note }}</div>
        </div>
      </v-alert>

      <div class="d-flex justify-space-between">
        <v-btn
          color="primary"
          outlined
          large
          :loading="canceling"
          @click="confirmCancelResignation"
        >
          <v-icon left>mdi-undo</v-icon>
          ยกเลิกการลาออก
        </v-btn>
        
        <v-btn
          color="info"
          text
          @click="showResignationDetails = !showResignationDetails"
        >
          <v-icon left>{{ showResignationDetails ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          {{ showResignationDetails ? 'ซ่อน' : 'แสดง' }}รายละเอียด
        </v-btn>
      </div>
      
      <!-- รายละเอียดเพิ่มเติม -->
      <v-expand-transition>
        <v-card v-show="showResignationDetails" outlined class="mt-4">
          <v-card-title class="info white--text">
            <v-icon left>mdi-information</v-icon>
            ข้อมูลการลาออกโดยละเอียด
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <div class="text-subtitle-2 mb-2">สถานะปัจจุบัน</div>
                <v-chip color="red" text-color="white">
                  <v-icon left small>mdi-account-off</v-icon>
                  ลาออกแล้ว
                </v-chip>
              </v-col>
              
              <v-col cols="12" md="6">
                <div class="text-subtitle-2 mb-2">วันที่บันทึก</div>
                <div>{{ formatDateTime(employee.updated_at) || 'ไม่ระบุ' }}</div>
              </v-col>
              
              <v-col cols="12">
                <div class="text-subtitle-2 mb-2">ข้อมูลการจ้างงาน</div>
                <v-simple-table dense>
                  <tbody>
                    <tr>
                      <td class="text-caption grey--text">วันที่เริ่มงาน:</td>
                      <td>{{ formatDate(employee.hire_date) || 'ไม่ระบุ' }}</td>
                    </tr>
                    <tr>
                      <td class="text-caption grey--text">วันที่ลาออก:</td>
                      <td>{{ formatDate(employee.resignation_date) }}</td>
                    </tr>
                    <tr>
                      <td class="text-caption grey--text">ระยะเวลารับราชการ:</td>
                      <td>{{ calculateWorkDuration() }}</td>
                    </tr>
                    <tr>
                      <td class="text-caption grey--text">ตำแหน่งสุดท้าย:</td>
                      <td>{{ employee.position_name || 'ไม่ระบุ' }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-expand-transition>
    </template>
  </v-card-text>
</template>

<script>
export default {
  name: 'ResignationTab',
  
  props: {
    employee: {
      type: Object,
      default: () => ({})
    },
    resigning: {
      type: Boolean,
      default: false
    },
    canceling: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      formValid: false,
      showResignationDetails: false,
      resignData: {
        date: '',
        reason: '',
        note: ''
      },
      
      resignReasons: [
        { text: 'ลาออกด้วยเหตุส่วนตัว', value: 'ลาออกด้วยเหตุส่วนตัว' },
        { text: 'ไปทำงานที่อื่น', value: 'ไปทำงานที่อื่น' },
        { text: 'เกษียณอายุราชการ', value: 'เกษียณอายุราชการ' },
        { text: 'ย้ายภูมิลำเนา', value: 'ย้ายภูมิลำเนา' },
        { text: 'ศึกษาต่อ', value: 'ศึกษาต่อ' },
        { text: 'ปัญหาสุขภาพ', value: 'ปัญหาสุขภาพ' },
        { text: 'เหตุครอบครัว', value: 'เหตุครอบครัว' },
        { text: 'ไม่พอใจสภาพการทำงาน', value: 'ไม่พอใจสภาพการทำงาน' },
        { text: 'อื่นๆ', value: 'อื่นๆ' }
      ],
      
      dateRules: [
        v => !!v || 'กรุณาระบุวันที่ลาออก',
        v => {
          if (!v) return true
          const selectedDate = new Date(v)
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          selectedDate.setHours(0, 0, 0, 0)
          return selectedDate <= today || 'วันที่ลาออกไม่สามารถเป็นวันในอนาคต'
        }
      ],
      
      noteRules: [
        v => !v || v.length <= 500 || 'หมายเหตุต้องไม่เกิน 500 ตัวอักษร',
        v => !v || v.trim().length > 0 || 'หมายเหตุไม่สามารถเป็นช่องว่าง'
      ]
    }
  },
  
  computed: {
    maxDate() {
      return new Date().toISOString().split('T')[0]
    }
  },
  
  methods: {
    handleResign() {
      if (this.$refs.resignForm.validate()) {
        this.$emit('resign', this.resignData)
      }
    },
    
    async confirmCancelResignation() {
      const confirmed = await this.confirmAction(
        'ยืนยันการยกเลิกการลาออก',
        `คุณแน่ใจหรือไม่ว่าต้องการยกเลิกการลาออกของ ${this.employee.full_name}?\n\nการดำเนินการนี้จะทำให้พนักงานกลับมาปฏิบัติงานได้อีกครั้ง`
      )
      
      if (confirmed) {
        this.$emit('cancel')
      }
    },
    
    resetForm() {
      this.resignData = {
        date: '',
        reason: '',
        note: ''
      }
      if (this.$refs.resignForm) {
        this.$refs.resignForm.resetValidation()
      }
    },
    
    calculateWorkDuration() {
      if (!this.employee.hire_date) return 'ไม่สามารถคำนวณได้'
      
      const startDate = new Date(this.employee.hire_date)
      const endDate = this.employee.resignation_date 
        ? new Date(this.employee.resignation_date) 
        : new Date()
      
      const diffTime = Math.abs(endDate - startDate)
      const years = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365.25))
      const months = Math.floor((diffTime % (1000 * 60 * 60 * 24 * 365.25)) / (1000 * 60 * 60 * 24 * 30.44))
      const days = Math.floor((diffTime % (1000 * 60 * 60 * 24 * 30.44)) / (1000 * 60 * 60 * 24))
      
      let result = []
      if (years > 0) result.push(`${years} ปี`)
      if (months > 0) result.push(`${months} เดือน`)
      if (days > 0 && years === 0) result.push(`${days} วัน`)
      
      return result.length > 0 ? result.join(' ') : 'น้อยกว่า 1 วัน'
    },
    
    async confirmAction(title, text) {
      if (this.$swal) {
        const result = await Swal.fire({
          title: title,
          text: text,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#1976d2',
          cancelButtonColor: '#757575',
          confirmButtonText: 'ยืนยัน',
          cancelButtonText: 'ยกเลิก',
          reverseButtons: true
        })
        return result.isConfirmed
      }
      return confirm(text)
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      try {
        return new Date(dateString).toLocaleDateString('th-TH', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      } catch {
        return dateString
      }
    },
    
    formatDateTime(dateString) {
      if (!dateString) return ''
      try {
        return new Date(dateString).toLocaleString('th-TH', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch {
        return dateString
      }
    }
  },
  
  watch: {
    employee: {
      handler() {
        // Reset form when employee data changes
        this.resetForm()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
/* Custom styling for resignation tab */
.v-alert .v-icon {
  align-self: flex-start;
}

.v-simple-table tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.02);
}

.v-simple-table tbody tr td {
  padding: 8px 16px;
}

.v-expansion-panels {
  box-shadow: none;
}

/* Warning alert styling */
.v-alert.v-alert--outlined.theme--light.warning {
  border-color: #ff9800;
  background-color: rgba(255, 152, 0, 0.05);
}

/* Form enhancements */
.v-text-field .v-input__slot {
  min-height: 48px;
}

.v-textarea textarea {
  line-height: 1.5;
}

/* Button group alignment */
.d-flex.justify-space-between .v-btn {
  min-width: 120px;
}

/* Card transitions */
.v-card {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition: all 0.3s ease;
}
</style>